import type { ReportLabel, ReportLabelTarget } from '@clockwise/clients/report-labels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Tag } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { reportLabelsApi } from '@/lib/api';
import { apiErrorText } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { MAX_LABEL_NAME } from './label-name-dialog';
import { useToggleLabelItem } from './use-library';

interface Props {
  /** The report being labelled — its name is the trigger's accessible name. */
  reportName: string;
  target: ReportLabelTarget;
  /** Every label the caller sees; the picker offers only the editable ones. */
  labels: readonly ReportLabel[];
  /** Ids of the labels this report carries now. */
  current: ReadonlySet<string>;
  /** Whether the report has any visible labels — decides the trigger's text. */
  hasLabels: boolean;
  /**
   * The row's title line — the name and its chips. The list opens anchored to
   * the START of this line rather than to the button: ticking a label adds a
   * chip, which pushes the button right, and a list anchored to it would jump
   * out from under the pointer between two ticks.
   */
  children: ReactNode;
}

/**
 * The label button on a report row, and the list it opens.
 *
 * Shows the caller's own labels and — only for someone who manages them —
 * the standard ones, each a tick to toggle. Typing a name that is not there
 * offers to create it as a personal label and file the report under it in
 * one go, which is how most labels will be born.
 *
 * Standard labels are offered with a line saying they ORGANISE, not share: a
 * manager ticking "Utilization" must not think they just showed the report to
 * the company. Who sees it is still decided by Share.
 */
export function LabelPicker({ reportName, target, labels, current, hasLabels, children }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const toggle = useToggleLabelItem();
  const queryClient = useQueryClient();

  const personal = labels.filter((l) => l.kind === 'personal');
  const standard = labels.filter((l) => l.kind === 'standard' && l.editable);
  const typed = query.trim();
  // Any label of this name, editable or not — a personal label may not reuse a
  // standard name, so offering to "create" it would be offering a refusal.
  const exists = labels.some(
    (l) => l.kind !== 'starred' && l.name.toLowerCase() === typed.toLowerCase(),
  );

  const create = useMutation({
    // ONE request: the label and its first report land together or not at
    // all, so a failure never leaves a label made but empty behind a toast
    // saying it failed.
    mutationFn: (name: string) => reportLabelsApi.create({ name, kind: 'personal', ...target }),
    onSuccess: () => setQuery(''),
    onError: (err) => toast.error(apiErrorText(err, 'Couldn’t create the label. Try again.')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.reportLabels.all }),
  });

  const item = (l: ReportLabel) => {
    const on = current.has(l.id);
    return (
      <CommandItem
        key={l.id}
        // Matched on the NAME alone (`keywords`, see the filter below) — an id
        // or a kind in the matched text would make "s" or "an" match every row.
        value={l.id}
        keywords={[l.name]}
        onSelect={() => toggle.mutate({ labelId: l.id, target, on: !on })}
        aria-checked={on}
      >
        <span
          className={cn(
            'flex size-4 items-center justify-center rounded-sm border',
            on ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
          )}
          aria-hidden
        >
          {on ? <Check className="size-3" /> : null}
        </span>
        <span className="truncate">{l.name}</span>
      </CommandItem>
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery('');
      }}
    >
      <PopoverAnchor asChild>
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          {children}
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Labels for ${reportName}`}
              // Hidden until the row is hovered or focused, like the star — but kept
              // in the tab order and always shown while open, so a keyboard user
              // can reach it and the trigger does not vanish under its own popover.
              className={cn(
                'inline-flex h-5 items-center gap-1 rounded border border-dashed px-1.5 text-muted-foreground text-xs transition-opacity hover:text-foreground',
                open
                  ? 'opacity-100'
                  : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 focus-visible:opacity-100',
              )}
            >
              <Tag className="size-3" aria-hidden />
              {hasLabels ? null : 'Add label'}
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent className="w-72 p-0" align="start">
        <Command
          // Filtering is cmdk's own; the create row below is outside its list
          // of matches, so it stays offered when nothing matches.
          filter={(_value, search, keywords) => {
            const needle = search.trim().toLowerCase();
            return (keywords ?? []).some((k) => k.toLowerCase().includes(needle)) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder="Find or create a label"
            value={query}
            onValueChange={(v) => setQuery(v.slice(0, MAX_LABEL_NAME))}
          />
          <CommandList>
            {/* Not while Create is offered — that row IS the answer to "no match". */}
            {typed && !exists ? null : <CommandEmpty>No labels yet.</CommandEmpty>}
            {personal.length > 0 && (
              <CommandGroup heading="Your labels">{personal.map(item)}</CommandGroup>
            )}
            {standard.length > 0 && (
              <CommandGroup heading="Standard">
                {standard.map(item)}
                <p className="px-2 pt-1 pb-1.5 text-muted-foreground text-xs">
                  Labels only organize. Who can see this report is set by Share.
                </p>
              </CommandGroup>
            )}
            {typed && !exists ? (
              <>
                <CommandSeparator />
                <CommandGroup forceMount>
                  <CommandItem
                    forceMount
                    value={`create:${typed}`}
                    disabled={create.isPending}
                    onSelect={() => create.mutate(typed)}
                  >
                    <Plus className="size-4" aria-hidden />
                    Create “{typed}”
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
